/**
 * Circular sun/moon icon switch — parity with the CSS toggleSwitch
 * (stacked icons, rotate+scale crossfade, press scale).
 * Self-contained; no new deps (RN + Reanimated + react-native-svg).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

const DEFAULT_SIZE = 56;
const ICON_VIEW = 24;
const TRANSFORM_MS = 500;
const OPACITY_MS = 300;
const PRESS_MS = 200;
const EASE = Easing.ease;

const COLORS = {
  moon_fg: '#212121',
  moon_bg: '#e8e8e8',
  sun_fg: '#e8e8e8',
  sun_bg: '#212121',
} as const;

const MOON_PATH =
  'M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z';

const SUN_PATH =
  'M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z';

export type SunMoonIconSwitchProps = {
  value?: boolean;
  default_value?: boolean;
  onValueChange?: (value: boolean) => void;
  disabled?: boolean;
  /** Outer diameter in px. Default 56 (CSS). */
  size?: number;
  style?: ViewStyle;
  accessibilityLabel?: string;
};

function MoonIcon({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${ICON_VIEW} ${ICON_VIEW}`}>
      <Path fillRule="evenodd" clipRule="evenodd" d={MOON_PATH} fill={color} />
    </Svg>
  );
}

function SunIcon({ size, color }: { size: number; color: string }) {
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${ICON_VIEW} ${ICON_VIEW}`}>
      <Path d={SUN_PATH} fill={color} />
    </Svg>
  );
}

export function SunMoonIconSwitch({
  value,
  default_value = false,
  onValueChange,
  disabled = false,
  size = DEFAULT_SIZE,
  style,
  accessibilityLabel = 'Toggle Theme',
}: SunMoonIconSwitchProps) {
  const is_controlled = value !== undefined;
  const [internal_value, set_internal_value] = useState(default_value);
  const checked = is_controlled ? Boolean(value) : internal_value;

  const transform_sv = useSharedValue(checked ? 1 : 0);
  const opacity_sv = useSharedValue(checked ? 1 : 0);
  const press_sv = useSharedValue(0);

  useEffect(() => {
    const to = checked ? 1 : 0;
    transform_sv.value = withTiming(to, { duration: TRANSFORM_MS, easing: EASE });
    opacity_sv.value = withTiming(to, { duration: OPACITY_MS, easing: EASE });
  }, [checked, opacity_sv, transform_sv]);

  const on_toggle = useCallback(() => {
    if (disabled) {
      return;
    }
    const next = !checked;
    if (!is_controlled) {
      set_internal_value(next);
    }
    onValueChange?.(next);
  }, [checked, disabled, is_controlled, onValueChange]);

  const on_press_in = useCallback(() => {
    if (disabled) {
      return;
    }
    press_sv.value = withTiming(1, { duration: PRESS_MS, easing: EASE });
  }, [disabled, press_sv]);

  const on_press_out = useCallback(() => {
    press_sv.value = withTiming(0, { duration: PRESS_MS, easing: EASE });
  }, [press_sv]);

  const host_style = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(press_sv.value, [0, 1], [1, 0.94]) }],
  }));

  // Unchecked → sun visible (rotate 360°, scale 1). Checked → hide.
  const sun_style = useAnimatedStyle(() => ({
    opacity: interpolate(opacity_sv.value, [0, 1], [1, 0]),
    transform: [
      { rotate: `${interpolate(transform_sv.value, [0, 1], [360, 0])}deg` },
      { scale: interpolate(transform_sv.value, [0, 1], [1, 0]) },
    ],
  }));

  // Checked → moon visible (rotate 360°, scale 1). Unchecked → hide.
  const moon_style = useAnimatedStyle(() => ({
    opacity: interpolate(opacity_sv.value, [0, 1], [0, 1]),
    transform: [
      { rotate: `${interpolate(transform_sv.value, [0, 1], [0, 360])}deg` },
      { scale: interpolate(transform_sv.value, [0, 1], [0, 1]) },
    ],
  }));

  const glyph = Math.round((32 / DEFAULT_SIZE) * size);

  return (
    <Pressable
      onPress={on_toggle}
      onPressIn={on_press_in}
      onPressOut={on_press_out}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{ checked, disabled }}
      accessibilityLabel={accessibilityLabel}
      style={style}>
      <Animated.View
        style={[
          styles.host,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            opacity: disabled ? 0.55 : 1,
          },
          host_style,
        ]}>
        <Animated.View
          pointerEvents="none"
          style={[
            styles.icon,
            {
              borderRadius: size / 2,
              backgroundColor: COLORS.moon_bg,
            },
            moon_style,
          ]}>
          <MoonIcon size={glyph} color={COLORS.moon_fg} />
        </Animated.View>

        <Animated.View
          pointerEvents="none"
          style={[
            styles.icon,
            {
              borderRadius: size / 2,
              backgroundColor: COLORS.sun_bg,
            },
            sun_style,
          ]}>
          <SunIcon size={glyph} color={COLORS.sun_fg} />
        </Animated.View>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  host: {
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
});

export default SunMoonIconSwitch;
